import { useRef, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { Textarea } from '@/components/ui/textarea.js';
import { QualityApiError, useCorrectOutcome } from '@/lib/api/quality.js';
import {
  CASE_OUTCOMES,
  type CaseOutcome,
  type TestResult,
} from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';
import { randomUuid } from '@/lib/random-uuid.js';

import {
  caseDisplayStatus,
  caseOutcome,
  outcomeLabel,
} from './quality-outcome.js';

export type CorrectableOutcome = Exclude<CaseOutcome, 'unresolved'>;

/** What the two Requires review choices mean, shown above the form they open. */
const CHOICE_INTRO: Partial<Record<CorrectableOutcome, string>> = {
  defect_found: 'Confirm that the application has a defect.',
  case_unclear:
    'Not a defect: Digital QA needs a clearer case. It is saved as a test case issue.',
};

/** What saving a Requires review choice does beyond this page, shown under the choice. */
const CHOICE_EFFECT =
  "Saving changes this case's outcome for everyone who opens the run and in a linked test management system. It is recorded in Correction history, and you can correct it again.";

export function correctionRequestId(): string {
  return randomUuid();
}

interface SubmittedCorrection {
  body: string;
  requestId: string;
}

interface OutcomeSelection {
  evidenceKey: string;
  outcome: CorrectableOutcome;
}

/** Manual outcome correction form with an idempotency key retained across exact-body retries. */
export function OutcomeCorrectionDialog({
  runId,
  result,
  reportChanged = false,
  initialOutcome,
  onClose,
}: {
  runId: string;
  result: TestResult;
  reportChanged?: boolean;
  /** The outcome a Requires review choice opened the form with. */
  initialOutcome?: CorrectableOutcome;
  onClose: () => void;
}): JSX.Element {
  const current = caseOutcome(result);
  const evidenceKey = `${result.case_id}:${result.outcome_revision ?? 'unknown'}:${current ?? 'unknown'}`;
  const [selection, setSelection] = useState<OutcomeSelection>(() => ({
    evidenceKey,
    outcome:
      initialOutcome ??
      (current && current !== 'unresolved' ? current : 'case_unclear'),
  }));
  const [missingReason, setMissingReason] = useState(false);
  const [acknowledgedConflict, setAcknowledgedConflict] =
    useState<unknown>(null);
  const [comment, setComment] = useState('');
  const submitted = useRef<SubmittedCorrection | null>(null);
  const correction = useCorrectOutcome(runId, result.case_id);

  const conflictRevision =
    correction.error instanceof QualityApiError &&
    correction.error.status === 409
      ? correction.error.details?.current_revision
      : null;
  const correctionConflict =
    correction.error instanceof QualityApiError &&
    correction.error.details?.code === 'outcome_revision_conflict';
  const requestConflict =
    correction.error instanceof QualityApiError &&
    correction.error.details?.code === 'correction_request_conflict';
  const resultNeedsReview = selection.evidenceKey !== evidenceKey;
  const conflictNeedsReview =
    (correctionConflict || requestConflict) &&
    acknowledgedConflict !== correction.error;
  const selectionNeedsReview = resultNeedsReview || conflictNeedsReview;
  const outcome = resultNeedsReview
    ? current && current !== 'unresolved'
      ? current
      : 'case_unclear'
    : selection.outcome;
  const forbidden =
    correction.error instanceof QualityApiError &&
    correction.error.status === 403;
  const normalizedComment = comment.trim();
  const commentValid =
    normalizedComment.length > 0 && normalizedComment.length <= 4_000;
  const revisionValid =
    Number.isInteger(result.outcome_revision) &&
    Number(result.outcome_revision) >= 1;

  return (
    <AppDialog
      open
      onOpenChange={(open) => {
        if (!open && !correction.isPending) onClose();
      }}
      title={`Correct outcome for ${result.title || result.case_id}`}
      description="Choose the current outcome and record why the evidence supports the change."
    >
      <div className="space-y-4">
        {initialOutcome && CHOICE_INTRO[initialOutcome] ? (
          <div className="space-y-1">
            <p className="text-sm text-foreground">
              {CHOICE_INTRO[initialOutcome]}
            </p>
            <p className="text-sm text-muted-foreground">{CHOICE_EFFECT}</p>
          </div>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Current outcome:{' '}
          {current
            ? outcomeLabel(caseDisplayStatus(result) as CaseOutcome)
            : 'Unavailable'}{' '}
          · revision {result.outcome_revision ?? '—'}
        </p>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Outcome</span>
          <Select
            value={outcome}
            onValueChange={(value) => {
              if (value === 'unresolved') return;
              setSelection({
                evidenceKey,
                outcome: value as CorrectableOutcome,
              });
              setAcknowledgedConflict(correction.error);
            }}
            disabled={correction.isPending}
          >
            <SelectTrigger className="w-full" aria-label="Outcome">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CASE_OUTCOMES.filter((value) => value !== 'unresolved').map(
                (value) => (
                  <SelectItem key={value} value={value}>
                    {outcomeLabel(value)}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </label>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Reason</span>
          <Textarea
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
              setMissingReason(false);
            }}
            placeholder="Explain why this is the correct outcome"
            aria-invalid={
              missingReason || (!commentValid && comment.length > 0)
            }
            maxLength={4_000}
            disabled={correction.isPending}
          />
        </label>
        {missingReason ? (
          <p role="alert" className="text-sm text-destructive">
            Add a reason for this outcome.
          </p>
        ) : null}
        {!current || !revisionValid ? (
          <p className="text-sm text-muted-foreground">
            This case has no final outcome to correct yet.
          </p>
        ) : null}
        {correctionConflict ? (
          <p className="rounded-md border border-warning-500/30 bg-warning-500/5 p-3 text-sm text-warning-700">
            This case changed while the dialog was open
            {typeof conflictRevision === 'number'
              ? ` (current revision ${conflictRevision})`
              : ''}
            . Review the updated outcome, then retry. Your reason has been kept.
          </p>
        ) : requestConflict ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            This correction needs a fresh review before it can be retried.
            Review the current outcome, then retry. Your reason has been kept.
          </p>
        ) : forbidden ? (
          <p className="text-sm text-muted-foreground">
            You can review this result, but you do not have permission to
            correct it. Your reason has been kept.
          </p>
        ) : correction.isError ? (
          <p className="text-sm text-destructive">
            {correction.error instanceof Error
              ? correction.error.message
              : 'The correction could not be saved.'}
          </p>
        ) : null}
        {selectionNeedsReview ? (
          <div className="rounded-md border border-warning-500/30 bg-warning-500/5 p-3 text-sm text-warning-700">
            <p>
              Confirm the current outcome before saving. Your reason has been
              kept.
            </p>
            {current && current !== 'unresolved' ? (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={correction.isPending}
                onClick={() => {
                  setSelection({ evidenceKey, outcome: current });
                  setAcknowledgedConflict(correction.error);
                }}
              >
                Confirm {outcomeLabel(current)}
              </Button>
            ) : null}
          </div>
        ) : null}
        {reportChanged ? (
          <p className="rounded-md border border-warning-500/30 bg-warning-500/5 p-3 text-sm text-warning-700">
            The report changed while this dialog was open. Your draft has been
            kept, and saving will be available after the current revision loads.
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={correction.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={() => {
              /** Kept enabled: each blocker below already has its message on screen. */
              if (
                forbidden ||
                !current ||
                !revisionValid ||
                selectionNeedsReview ||
                reportChanged ||
                correction.isPending
              )
                return;
              if (!commentValid) {
                setMissingReason(true);
                return;
              }
              const body = {
                outcome,
                comment: normalizedComment,
                expectedRevision: Number(result.outcome_revision),
              };
              const serializedBody = JSON.stringify(body);
              const previous = submitted.current;
              const requestId =
                previous && previous.body === serializedBody && !requestConflict
                  ? previous.requestId
                  : correctionRequestId();
              submitted.current = { body: serializedBody, requestId };
              correction.mutate(
                { ...body, requestId },
                {
                  onSuccess: () => {
                    notify.success(
                      initialOutcome === 'case_unclear' &&
                        outcome === 'case_unclear'
                        ? 'Saved as a test case issue. To improve the case, use Rework in the next steps for this run.'
                        : 'Outcome corrected.',
                    );
                    onClose();
                  },
                },
              );
            }}
          >
            {correction.isPending ? 'Saving…' : 'Save correction'}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
